<!--
DocumentUpload.vue - Componente subida de documentos para Olvidadme
Aplicando Component Pattern de Basket Base con drag & drop y validación

Contexto: Upload component para documentos legales con encriptación
- Integra con Document model con field-level encryption
- Input libre para descripcion del documento (customLabel)
- Aplica validación GDPR y compliance legal
- Drag & drop con preview y progress
-->

<template>
  <div class="document-upload-container">
    <!-- Upload area -->
    <div
      :class="uploadAreaClasses"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
      role="button"
      :aria-label="uploadAreaLabel"
      tabindex="0"
      @keydown.enter="triggerFileInput"
      @keydown.space.prevent="triggerFileInput"
    >
      <!-- Icon and text -->
      <div class="text-center">
        <div class="mb-4">
          <svg
            :class="iconClasses"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
            />
          </svg>
        </div>
        
        <div class="space-y-2">
          <p class="text-lg font-medium text-gray-900">
            {{ primaryText }}
          </p>
          <p class="text-sm text-gray-600">
            {{ secondaryText }}
          </p>
          <p class="text-xs text-gray-500">
            {{ restrictionsText }}
          </p>
        </div>
      </div>

      <!-- Hidden file input -->
      <input
        ref="fileInput"
        type="file"
        :multiple="allowMultiple"
        :accept="acceptedFormats"
        @change="handleFileSelect"
        class="hidden"
        :aria-label="fileInputLabel"
      />
    </div>

    <!-- File list -->
    <div v-if="files.length > 0" class="mt-6 space-y-3">
      <h4 class="text-sm font-medium text-gray-900">
        Archivos seleccionados ({{ files.length }})
      </h4>
      
      <div class="space-y-2">
        <div
          v-for="file in files"
          :key="file.id"
          :class="fileItemClasses(file)"
        >
          <!-- File info -->
          <div class="flex items-center flex-1 min-w-0">
            <div class="flex-shrink-0 mr-3">
              <component
                :is="getFileIconComponent(file)"
                class="w-6 h-6 text-gray-400"
                aria-hidden="true"
              />
            </div>
            
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-slate-900 truncate">
                {{ file.name }}
              </p>
              <div class="flex items-center flex-wrap gap-1.5 text-xs text-slate-500 mt-0.5">
                <span class="shrink-0">{{ formatFileSize(file.size) }}</span>
                <span
                  v-if="file.customLabel"
                  class="inline-block px-1.5 py-0.5 rounded-md bg-teal-50 text-teal-700 font-medium truncate max-w-[160px]"
                  :title="file.customLabel"
                >
                  {{ file.customLabel }}
                </span>
                <StatusBadge
                  v-if="file.status"
                  :status="file.status"
                  size="small"
                  variant="minimal"
                />
              </div>
            </div>
          </div>

          <!-- Progress bar -->
          <div v-if="file.status === 'uploading'" class="mt-2">
            <div class="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>Subiendo...</span>
              <span>{{ Math.round(file.progress || 0) }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-1">
              <div
                class="bg-blue-500 h-1 rounded-full transition-all duration-300"
                :style="{ width: `${file.progress || 0}%` }"
              ></div>
            </div>
          </div>

          <!-- Document description input -->
          <div v-if="file.status === 'processing' || file.status === 'processed'" class="mt-3">
            <label
              :for="`doc-label-${file.id}`"
              class="block text-xs font-medium text-slate-600 mb-1.5"
            >
              Descripcion del documento
              <span class="text-red-500 ml-0.5" aria-hidden="true">*</span>
              <span class="sr-only">(obligatorio)</span>
            </label>

            <input
              :id="`doc-label-${file.id}`"
              type="text"
              v-model="file.customLabel"
              @input="handleDocumentTypeChange(file)"
              @blur="validateCustomLabel(file)"
              placeholder="Ej: Certificado de empadronamiento, Contrato..."
              maxlength="100"
              :aria-describedby="`doc-hint-${file.id} doc-count-${file.id}`"
              :aria-invalid="file.labelError ? 'true' : undefined"
              autocomplete="off"
              spellcheck="false"
              :class="[
                'w-full px-4 py-3 bg-white border rounded-xl text-slate-900 text-sm',
                'placeholder:text-slate-400 transition-all duration-200',
                'focus:outline-none focus:ring-2',
                file.labelError
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                  : file.customLabel?.trim()
                    ? 'border-teal-400 focus:border-teal-500 focus:ring-teal-500/20'
                    : 'border-slate-200 focus:border-teal-500 focus:ring-teal-500/20'
              ]"
            />

            <p
              v-if="file.labelError"
              class="mt-1 text-xs text-red-600"
              role="alert"
            >
              {{ file.labelError }}
            </p>

            <div class="flex items-center justify-between mt-1.5">
              <p :id="`doc-hint-${file.id}`" class="text-xs text-slate-500">
                Describe brevemente este documento legal
              </p>
              <p
                :id="`doc-count-${file.id}`"
                class="text-xs tabular-nums shrink-0 ml-3"
                :class="(file.customLabel?.length || 0) >= 90 ? 'text-amber-600' : 'text-slate-500'"
                aria-live="polite"
                aria-atomic="true"
              >
                {{ file.customLabel?.length || 0 }}/100
              </p>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-3 mt-4 pt-3 border-t border-slate-100">
            <button
              v-if="file.status !== 'uploading'"
              @click="removeFile(file.id)"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <TrashIcon class="w-4 h-4" />
              Eliminar
            </button>

            <button
              v-if="file.status === 'rejected'"
              @click="retryFile(file.id)"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload button -->
    <div v-if="files.length > 0 && !isUploading" class="mt-6">
      <button
        @click="startUpload"
        :disabled="!canUpload"
        :class="uploadButtonClasses"
      >
        {{ uploadButtonText }}
      </button>
    </div>

    <!-- Error messages -->
    <div v-if="errors.length > 0" class="mt-4 space-y-2">
      <div
        v-for="error in errors"
        :key="error.id"
        class="bg-red-50 border border-red-200 rounded-md p-3"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-800">{{ error.message }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import StatusBadge from '../ui/StatusBadge.vue'
import { DocumentIcon, DocumentTextIcon, PhotoIcon, PaperClipIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { useDocumentsStore } from '@/stores/documents'
import { useResponsive } from '@/composables/useResponsive.js'

const props = defineProps({
  caseId: {
    type: String,
    required: true
  },
  maxFiles: {
    type: Number,
    default: 5
  },
  maxFileSize: {
    type: Number,
    default: 10 * 1024 * 1024 // 10MB
  },
  allowMultiple: {
    type: Boolean,
    default: true
  },
  acceptedTypes: {
    type: Array,
    default: () => ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx']
  },
  autoUpload: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['upload-start', 'upload-progress', 'upload-complete', 'upload-error'])

// Store
const documentsStore = useDocumentsStore()

const { isMobile, isTablet } = useResponsive()
const isSmallScreen = computed(() => isMobile.value || isTablet.value)

// Reactive state
const files = ref([])
const errors = ref([])
const isDragOver = ref(false)
const isUploading = ref(false)
const fileInput = ref(null)

// Computed properties
const acceptedFormats = computed(() => {
  return props.acceptedTypes.map(type => `.${type}`).join(',')
})

const uploadAreaClasses = computed(() => {
  const baseClasses = 'relative border-2 border-dashed rounded-lg p-12 text-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer transition-colors duration-200'
  const dragOverClasses = isDragOver.value ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
  const disabledClasses = files.length >= props.maxFiles ? 'opacity-50 cursor-not-allowed' : ''
  
  return [baseClasses, dragOverClasses, disabledClasses].filter(Boolean).join(' ')
})

const iconClasses = computed(() => {
  const baseClasses = 'mx-auto h-12 w-12'
  const colorClasses = isDragOver.value ? 'text-blue-400' : 'text-gray-400'
  
  return [baseClasses, colorClasses].join(' ')
})

const primaryText = computed(() => {
  if (files.length >= props.maxFiles) {
    return 'Límite de archivos alcanzado'
  }
  if (isSmallScreen.value) {
    return 'Pulsa para adjuntar un archivo o hacer una foto'
  }
  return 'Arrastra archivos aquí o haz clic para seleccionar'
})

const secondaryText = computed(() => {
  if (files.length >= props.maxFiles) {
    return `Máximo ${props.maxFiles} archivos permitidos`
  }
  if (isSmallScreen.value) {
    return `Puedes elegir desde tu galería o tomar una foto con la cámara. Máximo ${props.maxFiles} archivos, ${formatFileSize(props.maxFileSize)} por archivo`
  }
  return `Máximo ${props.maxFiles} archivos, ${formatFileSize(props.maxFileSize)} por archivo`
})

const restrictionsText = computed(() => {
  return `Formatos: ${props.acceptedTypes.join(', ').toUpperCase()}`
})

const uploadAreaLabel = computed(() => {
  return files.length >= props.maxFiles
    ? 'Límite de archivos alcanzado'
    : 'Seleccionar archivos para subir'
})

const fileInputLabel = computed(() => {
  return 'Seleccionar archivos'
})

const canUpload = computed(() => {
  return files.value.length > 0 &&
         files.value.every(file => file.customLabel?.trim().length >= 3) &&
         !isUploading.value
})

const uploadButtonClasses = computed(() => {
  const baseClasses = 'w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200'
  
  if (!canUpload.value) {
    return baseClasses + ' bg-gray-300 text-gray-500 cursor-not-allowed'
  }
  
  return baseClasses + ' bg-blue-600 text-white hover:bg-blue-700'
})

const uploadButtonText = computed(() => {
  const fileCount = files.value.length
  return `Subir ${fileCount} archivo${fileCount === 1 ? '' : 's'}`
})

// Methods
const triggerFileInput = () => {
  if (files.value.length < props.maxFiles) {
    fileInput.value?.click()
  }
}

const handleDragOver = (event) => {
  event.preventDefault()
  isDragOver.value = true
}

const handleDragLeave = (event) => {
  event.preventDefault()
  isDragOver.value = false
}

const handleDrop = (event) => {
  event.preventDefault()
  isDragOver.value = false
  
  const droppedFiles = Array.from(event.dataTransfer.files)
  addFiles(droppedFiles)
}

const handleFileSelect = (event) => {
  const selectedFiles = Array.from(event.target.files)
  addFiles(selectedFiles)
  
  // Reset input value
  event.target.value = ''
}

const addFiles = (newFiles) => {
  errors.value = []
  
  newFiles.forEach(file => {
    // Check file count limit
    if (files.value.length >= props.maxFiles) {
      addError(`Máximo ${props.maxFiles} archivos permitidos`)
      return
    }
    
    // Check file size
    if (file.size > props.maxFileSize) {
      addError(`"${file.name}" excede el tamaño máximo de ${formatFileSize(props.maxFileSize)}`)
      return
    }
    
    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    if (!props.acceptedTypes.includes(fileExtension)) {
      addError(`"${file.name}" no es un tipo de archivo permitido`)
      return
    }
    
    // Add file to list
    const fileObj = {
      id: generateId(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      status: 'processing',
      progress: 0,
      documentType: '',
      customLabel: '',
      labelError: null,
      uploadedAt: null
    }
    
    files.value.push(fileObj)
  })
  
  // Auto upload if enabled
  if (props.autoUpload && canUpload.value) {
    nextTick(() => {
      startUpload()
    })
  }
}

const handleDocumentTypeChange = (file) => {
  const selectedFile = files.value.find(f => f.id === file.id)
  if (!selectedFile) return

  selectedFile.labelError = null

  if (file.customLabel?.trim().length >= 3) {
    selectedFile.status = 'processed'
  } else {
    selectedFile.status = 'processing'
  }
}

const validateCustomLabel = (file) => {
  const label = file.customLabel?.trim() || ''
  const selectedFile = files.value.find(f => f.id === file.id)
  if (!selectedFile) return

  if (label.length === 0) {
    selectedFile.labelError = 'La descripcion del documento es obligatoria'
  } else if (label.length < 3) {
    selectedFile.labelError = 'La descripcion debe tener al menos 3 caracteres'
  } else {
    selectedFile.labelError = null
    selectedFile.status = 'processed'
  }
}

const removeFile = (fileId) => {
  const index = files.value.findIndex(f => f.id === fileId)
  if (index > -1) {
    files.value.splice(index, 1)
  }
  
  // Clear errors if no files
  if (files.value.length === 0) {
    errors.value = []
  }
}

const retryFile = (fileId) => {
  const file = files.value.find(f => f.id === fileId)
  if (file) {
    file.status = 'processing'
    file.progress = 0
  }
}

const startUpload = async () => {
  if (!canUpload.value) return
  
  isUploading.value = true
  emit('upload-start', files.value)
  
  try {
    for (const file of files.value) {
      if (file.status === 'processed') {
        await uploadFile(file)
      }
    }
    
    emit('upload-complete', files.value)
  } catch (error) {
    emit('upload-error', error)
    addError('Error durante la subida de archivos')
  } finally {
    isUploading.value = false
  }
}

const uploadFile = async (file) => {
  file.status = 'uploading'
  file.progress = 0

  try {
    // Real API call using documents store
    const onProgress = (progressEvent) => {
      if (progressEvent.total) {
        file.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
      }
    }

    // Upload using store action
    const uploadedDocument = await documentsStore.uploadDocument(
      props.caseId,
      file.file,
      'legal_document',
      onProgress,
      file.customLabel
    )

    file.status = 'verified'
    file.progress = 100
    file.uploadedAt = new Date()
    file.uploadedDocument = uploadedDocument

    emit('upload-progress', file)
  } catch (error) {
    file.status = 'rejected'
    file.progress = 0
    throw error
  }
}

// Utility functions
const addError = (message) => {
  errors.value.push({
    id: generateId(),
    message
  })
}

const generateId = () => {
  return Math.random().toString(36).substr(2, 9)
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const getFileIconComponent = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase()
  
  const iconMap = {
    pdf: DocumentIcon,
    doc: DocumentTextIcon,
    docx: DocumentTextIcon,
    jpg: PhotoIcon,
    jpeg: PhotoIcon,
    png: PhotoIcon,
    default: PaperClipIcon
  }
  
  return iconMap[extension] || iconMap.default
}

const fileItemClasses = (file) => {
  const baseClasses = 'bg-white border rounded-lg p-4'
  const statusClasses = {
    'processing': 'border-yellow-200 bg-yellow-50',
    'uploading': 'border-blue-200 bg-blue-50',
    'verified': 'border-green-200 bg-green-50',
    'rejected': 'border-red-200 bg-red-50'
  }
  
  return [baseClasses, statusClasses[file.status] || 'border-gray-200'].join(' ')
}
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Drag and drop animations */
.document-upload-container {
  @apply transition-all duration-200;
}

/* File item animations */
.file-item-enter-active, .file-item-leave-active {
  @apply transition-all duration-300;
}

.file-item-enter-from, .file-item-leave-to {
  @apply opacity-0 transform scale-95;
}

/* Progress bar animation */
.progress-bar {
  transition: width 0.3s ease-in-out;
}

/* Focus styles for accessibility */
input[type="text"]:focus-visible {
  @apply outline-none;
}

button:focus-visible {
  @apply outline-none ring-2 ring-teal-500 ring-offset-2 rounded;
}
</style>